import React, { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import { AlertTriangle, Check, Clock, MessageSquareOff, RefreshCw, PhoneCall } from "lucide-react";
import { handledErrorCopy, handledKey, handledRequest } from "../lib/smsHandled";
import { movedCopy } from "../lib/esignMoved";

// "SMS needs attention" — Contract §7, SMS Commit 2.
//
// PLACEMENT: a staff-wide view alongside the CRM Queue, reachable by sales
// and admin only. WS-Location users are location-scoped readers of their own
// patients; SMS operations are portal-wide (a failed text is WestStar's
// problem, not a site's), so a location-scoped slice here would be a
// half-truth. The API refuses that role outright.
//
// Three buckets, exactly §7: failed rows and expired rows (read from the
// sender's queue) and consent-blocked enqueues (no queue row exists — those
// come from the portal's own block log). `sent` is never rendered as
// "delivered", and `sending` shows as pending: the portal has no delivery
// receipts.

// #28: replies first - a patient wrote to us; then what the sender could not do.
const BUCKETS = [
  // P-6 (2026-10-10) + #27: a reply that is not YES / STOP / START, words
  // included, until someone marks it handled.
  { key: "replies", label: "Replies to read", icon: MessageSquareOff,
    tone: "text-blue-700 bg-blue-50 border-blue-200",
    blurb: "The patient wrote back (NO, HELP, a YES the portal could not apply, or their own words) — read it and call if needed." },
  { key: "failed", label: "Failed", icon: AlertTriangle,
    tone: "text-rose-700 bg-rose-50 border-rose-200",
    blurb: "The sender tried and could not deliver." },
  { key: "expired", label: "Expired", icon: Clock,
    tone: "text-amber-700 bg-amber-50 border-amber-200",
    blurb: "The window closed before the patient answered." },
  { key: "blocked", label: "No consent", icon: MessageSquareOff,
    tone: "text-slate-700 bg-slate-100 border-slate-200",
    blurb: "Nothing was queued — this patient has no textable consent." },
];

// #28: which group the page shows - remembered per browser (a convenience only).
const FILTER_KEY = "sms-needs-attention-filter";
const readFilter = () => {
  try {
    const v = window.localStorage.getItem(FILTER_KEY);
    return v === "all" || BUCKETS.some((b) => b.key === v) ? v : "all";
  } catch (e) {
    return "all";
  }
};
const saveFilter = (v) => {
  try { window.localStorage.setItem(FILTER_KEY, v); } catch (e) { /* private window: not remembered */ }
};

const REASON_COPY = {
  portal_block_no_consent_for_template: "No SMS consent on record for this template",
  portal_block_no_phone: "No phone number on the patient record",
  portal_block_phone_not_normalizable: "Phone number is not a usable US number",
  suppressed_opt_out_list: "Patient replied STOP — do not text",
  send_outcome_unknown: "Send outcome unknown — do not resend",
  portal_block_esign_door_failed: "E-sign paperwork could not be created — send the paperwork another way",
  portal_block_esign_not_configured: "E-sign is switched on but not set up — tell Manna",
  portal_block_esign_void_failed: "The old paperwork link could not be cancelled — no new link was sent; tell Manna",
  portal_block_esign_state_unknown: "E-sign could not read the earlier paperwork link — send the paperwork another way",
};

// E2b (Oct 20, 2026; Matt's review): a T-06 row is paperwork that did not go
// out before the first visit - say that first, then what to do. A T-06 block
// older than 21 days leaves the list (the server's T06_BLOCK_MAX_AGE_DAYS):
// the visit has happened and paper covered it (v1.2 wording: Step 7's packet
// goes to network clinics only; WestStar's own clinics do paper at check-in).
const T06_COPY = {
  portal_block_no_phone: "Paperwork not sent — no phone number on the card. Send the paperwork another way",
  portal_block_phone_not_normalizable: "Paperwork not sent — the phone number is not usable. Send the paperwork another way",
  portal_block_no_consent_for_template: "Paperwork not sent — the patient replied STOP. Send the paperwork another way",
  portal_block_esign_door_failed: "Paperwork not sent — the e-sign link could not be created. Send the paperwork another way",
  portal_block_esign_not_configured: "Paperwork not sent — e-sign is switched on but not set up. Tell Manna",
  portal_block_esign_void_failed: "Paperwork not re-sent — the old link could not be cancelled. Tell Manna",
  portal_block_esign_state_unknown: "Paperwork not sent — the earlier link's status could not be read. Send the paperwork another way",
};
// 2026-10-23 (the reschedule marker): the visit moved to where no new link can go, so its link was cancelled
const reasonCopy = (r) =>
  (r.template_id === "T-06" && T06_COPY[r.reason]) || REASON_COPY[r.reason] || movedCopy(r.reason)
  || r.reason || "No reason recorded";

export default function SmsNeedsAttention() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState("");
  const [confirmKey, setConfirmKey] = useState(null);
  const [filter, setFilterState] = useState(readFilter);
  const setFilter = (v) => { setFilterState(v); saveFilter(v); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/crm/sms/needs-attention");
      setData(res.data);
    } catch (err) {
      setData({ available: false, failed: [], expired: [], blocked: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retry = async (rowId) => {
    setBusyId(rowId);
    try {
      await api.post(`/crm/sms/needs-attention/${rowId}/retry`);
      setToast("Queued again — the sender will pick it up on its next pass.");
      await load();
    } catch (err) {
      setToast("That row can no longer be retried.");
    } finally {
      setBusyId(null);
      setTimeout(() => setToast(""), 4000);
    }
  };

  // Wave 2 (2026-10-05): a person's decision, never an age limit. Two clicks
  // instead of a browser pop-up (inside Teams those can be silently
  // suppressed). The route comes from the BUCKET (see lib/smsHandled).
  const markHandled = async (bucket, row) => {
    const req = handledRequest(bucket, row);
    if (!req) return;
    setBusyId(handledKey(bucket, row));
    let status;
    try {
      await api.post(req.url, req.body);
      setToast("Marked handled.");
    } catch (err) {
      status = err && err.response ? err.response.status : -1;
      setToast(handledErrorCopy(status));
    } finally {
      setBusyId(null);
      setConfirmKey(null);
      setTimeout(() => setToast(""), 4000);
    }
    if (status === undefined || status === 404 || status === 409) await load();
  };

  const total = data
    ? (data.failed?.length || 0) + (data.expired?.length || 0) + (data.blocked?.length || 0) + (data.replies?.length || 0)
    : 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sms-needs-attention">
      <div className="px-8 pt-8 pb-4 border-b border-slate-200 bg-white">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          SMS needs attention
        </h1>
        <p className="mt-3 text-base text-slate-500 max-w-2xl">
          Texts that did not reach a patient, and patients we are not allowed
          to text. Everything here needs a person — a retry where that is
          safe, otherwise a phone call. Once a person has dealt with an
          item, mark it handled; it comes back only if something new happens
          to it.
        </p>
        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={load}
            data-testid="sms-refresh-btn"
            className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 transition-colors"
          >
            <RefreshCw size={15} /> Refresh
          </button>
          <span className="text-sm text-slate-400" data-testid="sms-total-count">
            {loading ? "Loading…" : `${total} item${total === 1 ? "" : "s"}`}
          </span>
        </div>
        {/* #28: click a group to see only it; All shows everything. */}
        <div className="mt-5 flex flex-wrap items-center gap-2" role="tablist" data-testid="sms-filter-row">
          {[{ key: "all", label: "All", n: total }, ...BUCKETS.map((b) => ({ key: b.key, label: b.label, n: (data && data[b.key] ? data[b.key].length : 0) }))].map((f) => (
            <button
              key={f.key}
              role="tab"
              aria-selected={filter === f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`sms-filter-${f.key}`}
              className={`inline-flex items-center gap-2 px-3 h-9 rounded-full border text-sm font-semibold transition-colors ${
                filter === f.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
              }`}
            >
              {f.label}
              <span className={`text-xs ${filter === f.key ? "text-slate-300" : "text-slate-400"}`}>
                {loading ? "…" : f.n}
              </span>
            </button>
          ))}
        </div>
      </div>

      {!loading && data && data.available === false && (
        <div className="mx-8 mt-8 rounded-xl border border-slate-200 bg-white p-6"
             data-testid="sms-queue-unavailable">
          <p className="text-base font-semibold text-slate-800">
            The SMS queue is not reachable from this environment.
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Failed and expired rows live in the sender's table. Consent blocks
            below are the portal's own record and are unaffected.
          </p>
        </div>
      )}

      <div className="p-8 space-y-10">
        {BUCKETS.filter(({ key }) => filter === "all" || filter === key).map(({ key, label, icon: Icon, tone, blurb }) => {
          const rows = (data && data[key]) || [];
          return (
            <section key={key} data-testid={`sms-bucket-${key}`}>
              <div className="flex items-baseline gap-3">
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-semibold ${tone}`}>
                  <Icon size={14} /> {label}
                </span>
                <span className="text-sm text-slate-400" data-testid={`sms-count-${key}`}>
                  {rows.length}
                </span>
                <span className="text-sm text-slate-400">· {blurb}</span>
              </div>

              {rows.length === 0 ? (
                <p className="mt-4 text-sm text-slate-400" data-testid={`sms-empty-${key}`}>
                  Nothing here right now.
                </p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {rows.map((r) => (
                    <li
                      key={r.row_id || r.dedupe_key || `reply-${r.reply_id}`}
                      data-testid={`sms-item-${r.row_id || r.dedupe_key || `reply-${r.reply_id}`}`}
                      className="bg-white border border-slate-200 rounded-xl p-5 flex items-start justify-between gap-6 hover:border-slate-300 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900">
                          {r.patient_name
                            || (key === "replies" && !r.patient_id
                              ? <span data-testid={`sms-reply-unmatched-${r.reply_id}`}>Couldn't match a card{r.number_ends ? ` · number ends ${r.number_ends}` : ""}</span>
                              : "(patient not found)")}
                          <span className="ml-2 text-xs font-mono text-slate-400">
                            #{r.patient_id ?? "—"}
                          </span>
                          {key === "replies" && r.kind && r.kind !== "unknown" ? (
                            <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold uppercase text-amber-800" data-testid={`sms-reply-kind-${r.reply_id}`}>
                              {r.kind}
                            </span>
                          ) : null}
                        </p>
                        {key === "replies" ? (
                          <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap break-words" data-testid={`sms-reply-words-${r.reply_id}`}>
                            “{r.words || ""}”
                            {r.template_id ? <span className="ml-2 font-mono text-xs text-slate-400">answers {r.template_id}</span> : null}
                          </p>
                        ) : (
                        <p className="mt-1 text-sm text-slate-500">
                          <span className="font-mono">{r.template_id}</span>
                          {" · "}
                          {reasonCopy(r)}
                        </p>
                        )}
                        <p className="mt-1 text-xs text-slate-400">
                          {new Date(r.when).toLocaleString()}
                          {r.phone ? ` · ${r.phone}` : ""}
                        </p>
                      </div>
                      <div className="shrink-0 flex flex-col items-end gap-2">
                      {r.retry_allowed ? (
                        <button
                          onClick={() => retry(r.row_id)}
                          disabled={busyId === r.row_id}
                          data-testid={`sms-retry-btn-${r.row_id}`}
                          className="shrink-0 inline-flex items-center gap-2 px-4 h-9 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                          <RefreshCw size={14} />
                          {busyId === r.row_id ? "Queuing…" : "Send again"}
                        </button>
                      ) : (
                        <span
                          data-testid={`sms-call-instead-${r.row_id || r.dedupe_key}`}
                          className="shrink-0 inline-flex items-center gap-2 text-sm font-semibold text-slate-500"
                        >
                          <PhoneCall size={14} /> Call the patient
                        </span>
                      )}
                      {handledRequest(key, r) && (
                        confirmKey === handledKey(key, r) ? (
                          <span className="inline-flex items-center gap-2"
                                data-testid={`sms-handled-confirm-${r.row_id || r.dedupe_key}`}>
                            <button
                              onClick={() => markHandled(key, r)}
                              disabled={busyId === handledKey(key, r)}
                              data-testid={`sms-handled-yes-${r.row_id || r.dedupe_key}`}
                              className="inline-flex items-center gap-2 px-4 h-9 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                            >
                              <Check size={14} />
                              {busyId === handledKey(key, r) ? "Saving…" : "Yes, handled"}
                            </button>
                            <button
                              onClick={() => setConfirmKey(null)}
                              disabled={busyId === handledKey(key, r)}
                              data-testid={`sms-handled-cancel-${r.row_id || r.dedupe_key}`}
                              className="text-sm font-semibold text-slate-500 hover:text-slate-700 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            onClick={() => setConfirmKey(handledKey(key, r))}
                            data-testid={`sms-handled-btn-${r.row_id || r.dedupe_key}`}
                            className="inline-flex items-center gap-2 px-4 h-9 rounded-full border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-100 transition-colors"
                          >
                            <Check size={14} /> Mark handled
                          </button>
                        )
                      )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 bg-slate-900 text-white text-sm px-5 py-3 rounded-xl shadow-xl"
             data-testid="sms-toast">
          {toast}
        </div>
      )}
    </div>
  );
}
